// ** React Imports
import { cloneElement, useEffect } from 'react'
import { NavLink, useLocation, useMatch } from 'react-router-dom'

// ** Third Party Components
import { Badge } from 'dsm-react-lib'
import classnames from 'classnames'

// ** Vertical Menu Array Of Items
import { useVerticalLayout } from '@navigation/vertical'

// ** Utils
import { search, getAllParents } from '@layouts/utils'

const VerticalNavMenuLink = ({
  item,
  groupActive,
  setGroupActive,
  activeItem,
  setActiveItem,
  groupOpen,
  setGroupOpen,
  toggleActiveGroup,
  parentItem,
  routerProps,
  currentActiveItem,
  closeDropdown,
  menuCollapsed
}) => {
  // ** Conditional Link Tag, if item has newTab or externalLink props use <a> tag else use NavLink
  const LinkTag = item.externalLink ? 'a' : NavLink
  // const { verticalNavItems } = useVerticalLayout()
  const { newVerticalNavItem } = useVerticalLayout()
  const isSubMenu =
    parentItem !== undefined && groupOpen.length > 0 && groupOpen.includes(parentItem.id)
  const isCollapsed = menuCollapsed && !isSubMenu

  // ** URL Vars
  const location = useLocation()
  const currentURL = location.pathname
  // Use exact match or more precise matching instead of startsWith to prevent wrong active states
  const match = useMatch(item?.navLink || '')
  const hasNavLink =
    item.navLink === currentURL ||
    (item.navLink !== '/' && currentURL.startsWith(`${item.navLink}/`)) ||
    (match && match.pathname === item.navLink)

  // Check if this item is active - prioritize exact match over activeItem state
  const isActive = hasNavLink

  let activeItemValue = currentActiveItem
  if ((match && match.pathname === item.navLink) || (match === null && hasNavLink)) {
    activeItemValue = item.navLink
  }
  const searchParents = (navigation, currentURL) => {
    const parents = search(navigation, currentURL, routerProps) // Search for parent object
    const allParents = getAllParents(parents, 'id') // Parents Object to Parents Array
    return allParents
  }

  // ** URL Vars
  const resetActiveGroup = (navLink) => {
    const parents = search(newVerticalNavItem, navLink, match)
    toggleActiveGroup(item.id, parents)
  }

  // ** Reset Active & Open Group Arrays
  const resetActiveAndOpenGroups = () => {
    setGroupActive([])
    setGroupOpen([])
  }

  // ** Checks url & updates active item
  useEffect(() => {
    if (currentActiveItem !== null) {
      setActiveItem(currentActiveItem)
      const arr = searchParents(newVerticalNavItem, currentURL)
      setGroupActive([...arr])
    }
  }, [location])

  return (
    <li
      className={`${item.tourClass} ${classnames({
        'nav-item': !item.children,
        disabled: item.disabled,
        active: isActive || null
      })} !py-[3px] `}>
      <LinkTag
        data-testid={`leftNav-${item?.id}`}
        className={`!flex align-items-center !p-[8px] gap-[10px] focus:outline-none rounded-[4px] ${
          isActive
            ? '!bg-[var(--nav-active-bg)] active:!bg-[var(--nav-active-bg)] focus:!bg-[var(--nav-active-bg)]'
            : 'hover:bg-[var(--nav-hover-bg)] focus:!bg-transparent'
        } ${isCollapsed ? 'menuCollapse justify-content-center' : ''}`}
        target={item?.newTab ? '_blank' : undefined}
        {...(item?.externalLink === true
          ? {
              href: item?.navLink || '/'
            }
          : {
              to: item?.navLink || '/',
              state: item?.id === 'ali' ? { isNavClicked: true } : null // Pass state for tracking nav click
            })}
        onClick={(e) => {
          // Close dropdown when child menu item is clicked FIRST
          if (closeDropdown && typeof closeDropdown === 'function') {
            closeDropdown()
          }

          // Handle custom onClick first if provided
          if (item?.onClick && typeof item?.onClick === 'function') {
            e.preventDefault()
            item.onClick(e)
            return
          }

          if (!item?.navLink.length) {
            e.preventDefault()
          }

          // Reset active groups for standalone items only
          // If this is a standalone item (no parent), clear all active/open groups
          if (!parentItem) {
            resetActiveAndOpenGroups()
          } else {
            // If this is a child item in a group, update the active item and parent groups
            const arr = searchParents(newVerticalNavItem, item.navLink)
            setGroupActive([...arr])
          }
        }}>
        {cloneElement(item?.icon, {
          color: isActive ? 'var(--nav-active-text)' : 'var(--nav-icon)'
        })}
        {!isCollapsed && (
          <span
            className={`menu-item text-truncate ${
              isActive ? 'text-[var(--nav-active-text)]' : 'text-[var(--nav-text)]'
            }`}>
            {item?.title}
          </span>
        )}

        {!isCollapsed && item?.badge && item?.badgeText ? (
          <Badge className="ms-auto me-1" color={item?.badge} type="pill" label={item?.badgeText} />
        ) : null}
      </LinkTag>
    </li>
  )
}

export default VerticalNavMenuLink
